import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import VoiceRecorder from './VoiceRecorder';
import { getSessionId } from '../api/voice';
import './Dashboard.css';
import SportNavigation from './SportNavigation';
import { InternationalSection } from './International';

const STORAGE_KEYS = {
  raceType: 'racing_ui_race_type',
  dateFilter: 'racing_ui_date_filter',
  selectedDate: 'racing_ui_selected_date',
  searchTerm: 'racing_ui_search_term'
} as const;

// Harness/pace markers — used to separate thoroughbred from harness meetings.
const HARNESS_RE = /\b(pace|paced|pacing|trot|trotting|mobile|harness)\b/i;

const getStoredValue = (key: string, fallback = '') => {
  try {
    const value = localStorage.getItem(key);
    return value ?? fallback;
  } catch {
    return fallback;
  }
};

const getStoredDateFilter = (): 'yesterday' | 'today' | 'tomorrow' => {
  const value = getStoredValue(STORAGE_KEYS.dateFilter, 'tomorrow');
  return value === 'yesterday' || value === 'today' || value === 'tomorrow' ? value : 'tomorrow';
};

const getStoredRaceType = (): 'thoroughbred' | 'all' => {
  const value = getStoredValue(STORAGE_KEYS.raceType, 'thoroughbred');
  return value === 'all' ? 'all' : 'thoroughbred';
};

interface Race {
  race_number: number;
  race_name: string;
  race_class_conditions: string;
  race_status: string;
  race_distance: number;
  betfair_start_time?: string | null;
  race_completed?: boolean;
  result_state?: string;
  results_source?: string | null;
  finishers?: {position: number; number: number; runner: string; core_rank: number | null; brain_top?: boolean}[];
}

interface Meeting {
  _id: string;
  meeting_name: string;
  location: string;
  meeting_date: string;
  track_condition: string;
  venue_code: string;
  races: Race[];
}

const Dashboard: React.FC = () => {
  const location = useLocation();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [meetingError, setMeetingError] = useState('');
  const meetingRequest = useRef(0);
  const [raceType, setRaceType] = useState<'thoroughbred' | 'all'>(() => getStoredRaceType());
  const [dateFilter, setDateFilter] = useState<'yesterday' | 'today' | 'tomorrow'>(() => getStoredDateFilter());
  const [selectedDate, setSelectedDate] = useState(() => getStoredValue(STORAGE_KEYS.selectedDate, ''));
  const [searchTerm, setSearchTerm] = useState(() => getStoredValue(STORAGE_KEYS.searchTerm, ''));
  const [availableDates, setAvailableDates] = useState<string[]>([]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.raceType, raceType);
      localStorage.setItem(STORAGE_KEYS.dateFilter, dateFilter);
      localStorage.setItem(STORAGE_KEYS.selectedDate, selectedDate);
      localStorage.setItem(STORAGE_KEYS.searchTerm, searchTerm);
    } catch {
      // Ignore storage errors in restricted browser modes
    }
  }, [raceType, dateFilter, selectedDate, searchTerm]);

  // Prefill the search box when arrived via a ?search= link (e.g. a track name
  // clicked on a horse profile).
  useEffect(() => {
    const term = new URLSearchParams(location.search).get('search');
    if (term) setSearchTerm(term);
  }, [location.search]);

  useEffect(() => {
    fetchMeetings();
    const timer = setInterval(() => fetchMeetings(true), 60000);
    return () => { clearInterval(timer); meetingRequest.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter, selectedDate]);

  useEffect(() => {
    fetchAvailableDates();
  }, []);


  const formatLocalDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDateFilterForSelectedDate = (dateStr: string) => {
    if (!dateStr) return null;
    const selected = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(selected.getTime())) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selected.getTime() === today.getTime()) return 'today';
    if (selected.getTime() < today.getTime()) return 'yesterday';
    return 'tomorrow';
  };

  const getTargetDateString = () => {
    const today = new Date();
    if (dateFilter === 'yesterday') {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return formatLocalDate(yesterday);
    } else if (dateFilter === 'tomorrow') {
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      return formatLocalDate(tomorrow);
    }
    return formatLocalDate(today);
  };

  const getMaxSelectableDateString = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return formatLocalDate(tomorrow);
  };

  const maxSelectableDate = getMaxSelectableDateString();

  useEffect(() => {
    if (selectedDate && selectedDate > maxSelectableDate) {
      setSelectedDate('');
    }
  }, [selectedDate, maxSelectableDate]);

  useEffect(() => {
    if (!selectedDate) return;
    const nextFilter = getDateFilterForSelectedDate(selectedDate);
    if (nextFilter && nextFilter !== dateFilter) {
      setDateFilter(nextFilter);
    }
  }, [selectedDate, dateFilter]);

  const getDateLabel = () => {
    if (selectedDate) return 'Selected Date';
    if (dateFilter === 'yesterday') return 'Yesterday';
    if (dateFilter === 'tomorrow') return 'Tomorrow';
    return 'Today';
  };

  const getEffectiveDateString = () => {
    return selectedDate || getTargetDateString();
  };

  const fetchMeetings = async (background = false) => {
    const version = ++meetingRequest.current;
    if (!background) { setLoading(true); setMeetings([]); }
    try {
      const targetDate = getEffectiveDateString();
      const response = await axios.get(`${API_BASE_URL}/api/meetings/date/${targetDate}`, {params: {betfair_only: '1'}, timeout: 20000});
      if (version !== meetingRequest.current) return;
      setMeetings(response.data || []);
      setMeetingError('');
    } catch (error) {
      if (version !== meetingRequest.current) return;
      setMeetingError('Unable to refresh Betfair-listed meetings. Retrying automatically.');
    } finally {
      if (version === meetingRequest.current) setLoading(false);
    }
  };

  const fetchAvailableDates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/meeting-dates`);
      const dates = Array.isArray(response.data) ? response.data : [];
      setAvailableDates(dates);
    } catch (error) {
      console.error('Error fetching available dates:', error);
      setAvailableDates([]);
    }
  };

  const formatDateDisplay = (dateString: string) => {
    const [year, month, day] = dateString.split('-');
    if (!year || !month || !day) return dateString;
    return `${day}-${month}-${year}`;
  };

  const isHarnessRace = (race: Race) =>
    HARNESS_RE.test(`${race.race_name || ''} ${race.race_class_conditions || ''}`);

  // A meeting counts as harness only when every race is a pace/trot, so mixed
  // cards aren't hidden by the thoroughbred filter.
  const isHarnessMeeting = (meeting: Meeting) =>
    (meeting.races?.length ?? 0) > 0 && meeting.races.every(isHarnessRace);

  const typeFilteredMeetings = raceType === 'all'
    ? meetings
    : meetings.filter((meeting) => !isHarnessMeeting(meeting));

  const normalizedQuery = searchTerm.trim().toLowerCase();
  const filteredMeetings = normalizedQuery.length === 0
    ? typeFilteredMeetings
    : typeFilteredMeetings.filter((meeting) => {
        const meetingFields = [
          meeting.meeting_name,
          meeting.location,
          meeting.venue_code
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        const raceMatch = meeting.races?.some((race) => {
          const raceFields = [
            race.race_name,
            race.race_class_conditions,
            `r${race.race_number}`
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return raceFields.includes(normalizedQuery);
        });

        return meetingFields.includes(normalizedQuery) || raceMatch;
      });

  const formatRaceClass = (raceClass: string = '', raceName: string = '') => {
    if (!raceClass && !raceName) return '';
    
    const text = raceClass || raceName || '';

    // Australian racing terminology formatting
    return text
      .replace(/\bClass\b/gi, 'CL')
      .replace(/\bHandicap\b/gi, 'Hcp')
      .replace(/\bMaiden\b/gi, 'Maiden')
      .replace(/\bPlate\b/gi, 'Plte')
      .replace(/\bBenchmark\b/gi, 'BM')
      .replace(/\bFillies\s*&\s*Mares\b/gi, 'F&M')
      .replace(/\bNo-Metropolitan-Wins\b/gi, 'No-Metro-Wins')
      .replace(/\b3\s*Year\s*Old\b/gi, '3YO')
      .replace(/\b2\s*Year\s*Old\b/gi, '2YO')
      .replace(/\bLast\s*Year\b/gi, 'Last Ye')
      .replace(/\bRising\b/gi, 'Rising')
      .replace(/[;,\s]+$/, '')      // strip trailing punctuation/space (e.g. "Maiden;")
      .trim()
      .substring(0, 20);
  };

  const raceColumnCount = filteredMeetings.reduce((max, meeting) =>
    meeting.races.reduce((n, race) => Math.max(n, race.race_number), max), 1);

  if (loading) {
    return <div className="loading">Loading racing data...</div>;
  }

  return (
    <div className="dashboard">
      <SportNavigation />
      <div className="dashboard-title">
        <h2>Australian Horse Racing Meetings</h2>
        <button
          type="button"
          className={`race-type-indicator${raceType === 'thoroughbred' ? ' active' : ''}`}
          onClick={() => setRaceType((t) => (t === 'thoroughbred' ? 'all' : 'thoroughbred'))}
          title="Toggle between thoroughbred-only and all racing (incl. harness)"
        >
          {raceType === 'thoroughbred' ? '🐴 Thoroughbred Racing' : '🏇 All Racing'}
        </button>
      </div>
      <details className="meeting-voice-notes"><summary>Session voice notes</summary>
      <VoiceRecorder
        scope="session"
        context={{ sessionId: getSessionId() }}
        title="Session voice notes"
        subtitle="Record your form analysis for the whole session"
      />
      </details>
      <div className="dashboard-header">
        <div className="date-filter">
          <button 
            className={dateFilter === 'yesterday' ? 'active' : ''}
            onClick={() => {
              setDateFilter('yesterday');
              setSelectedDate('');
            }}
          >
            Yesterday
          </button>
          <button 
            className={dateFilter === 'today' ? 'active' : ''}
            onClick={() => {
              setDateFilter('today');
              setSelectedDate('');
            }}
          >
            Today
          </button>
          <button 
            className={dateFilter === 'tomorrow' ? 'active' : ''}
            onClick={() => {
              setDateFilter('tomorrow');
              setSelectedDate('');
            }}
          >
            Tomorrow
          </button>
        </div>

        <div className="search-controls">
          <div className="date-picker-wrapper">
            <select
              className="date-picker-input"
              value={selectedDate}
              onChange={(event) => {
                const nextDate = event.target.value;
                setSelectedDate(nextDate);
                const nextFilter = getDateFilterForSelectedDate(nextDate);
                if (nextFilter) {
                  setDateFilter(nextFilter);
                }
              }}
              aria-label="Select from available dates"
            >
              <option value="">Select available date</option>
              {availableDates.map((date) => (
                <option key={date} value={date}>
                  {formatDateDisplay(date)}
                </option>
              ))}
            </select>
            <input
              className="date-picker-input"
              type="date"
              value={selectedDate}
              max={maxSelectableDate}
              onChange={(event) => {
                const nextDate = event.target.value;
                if (!nextDate || nextDate <= maxSelectableDate) {
                  setSelectedDate(nextDate);
                  const nextFilter = getDateFilterForSelectedDate(nextDate);
                  if (nextFilter) {
                    setDateFilter(nextFilter);
                  }
                }
              }}
              aria-label="Select meeting date"
            />
            {selectedDate.length > 0 && (
              <button
                className="date-clear"
                onClick={() => {
                  setSelectedDate('');
                  setDateFilter('today');
                }}
              >
                Reset
              </button>
            )}
          </div>

          <div className="search-bar">
            <input
              className="search-input"
              type="text"
              placeholder="Search track, venue, race"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            {searchTerm.length > 0 && (
              <button className="search-clear" onClick={() => setSearchTerm('')}>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      <p className="info-note">Betfair-listed meetings only · Times in Sydney · Updates every minute.</p>
      <div className="result-legend"><span>Results: 1st → 4th</span><span className="core-rank-1">Core 1</span><span className="core-rank-2">Core 2</span><span className="core-rank-3">Core 3</span><span className="brain-top-chip">Brain 1</span><span className="brain-both-chip">Core + Brain</span></div>
      {meetingError && <p role="alert">{meetingError}</p>}
      {filteredMeetings.length === 0 ? (
        <div className="no-data">
          {meetings.length === 0 ? (
            <p>No Betfair-listed meetings found for {getDateLabel()} ({getEffectiveDateString()}).</p>
          ) : (
            <p>No meetings match "{searchTerm}" for {getDateLabel()} ({getEffectiveDateString()}).</p>
          )}
          <p className="info-note">Meetings appear when their Betfair markets are recorded. Completed meetings remain visible for that date.</p>
        </div>
      ) : (
        <div className="racing-grid">
          <table role="table" className="racing-table" style={{minWidth: Math.max(900, 150 + raceColumnCount * 116)}}>
            <thead role="rowgroup">
              <tr role="row">
                <th className="track-header">Track</th>
                {Array.from({ length: raceColumnCount }, (_, i) => (
                  <th key={`R${i + 1}`} className="race-header">R{i + 1}</th>
                ))}
              </tr>
            </thead>
            <tbody role="rowgroup">
              {filteredMeetings.map((meeting) => {
                // Create array of races indexed by race_number
                const raceMap: { [key: number]: Race } = {};
                meeting.races.forEach(race => {
                  raceMap[race.race_number] = race;
                });

                return (
                  <tr role="row" key={meeting._id} className="meeting-row">
                    <td role="cell" className="track-name">
                      <div className="track-info">
                        <span className="meeting-name">{meeting.meeting_name}</span>
                        {meeting.venue_code && (
                          <span className="venue-code">({meeting.venue_code})</span>
                        )}
                        {meeting.track_condition &&
                          meeting.track_condition.trim().toUpperCase() !== 'N/A' && (
                          <div className="track-condition">{meeting.track_condition}</div>
                        )}
                      </div>
                    </td>
                    {Array.from({ length: raceColumnCount }, (_, i) => {
                      const raceNumber = i + 1;
                      const race = raceMap[raceNumber];
                      
                      return (
                        <td role="cell" key={`${meeting._id}-R${raceNumber}`} className={`race-cell${!race ? " race-cell-empty" : ""}${race?.race_completed ? " race-cell-completed" : ""}`}>
                          {race ? (
                            <Link
                              to={`/race/${encodeURIComponent(meeting.meeting_name)}/${race.race_number}?date=${encodeURIComponent(getEffectiveDateString())}`}
                              className="race-link"
                              aria-label={`${meeting.meeting_name} race ${race.race_number}`}
                            >
                              <span className="mobile-race-number">Race {race.race_number}</span>
                              {race.betfair_start_time && Number.isFinite(Date.parse(race.betfair_start_time))
                                ? <time className="race-time" dateTime={race.betfair_start_time}>{new Intl.DateTimeFormat('en-AU',{timeZone:'Australia/Sydney',hour:'2-digit',minute:'2-digit',hour12:false}).format(new Date(race.betfair_start_time))}</time>
                                : <span className="race-time race-time-unknown">Time unavailable</span>}
                              {race.race_completed ? (
                                race.result_state === 'abandoned' ? <span className="race-result-status">Abandoned</span> :
                                <div className="race-results" aria-label="Finishing order: first to fourth">
                                  {[1, 2, 3, 4].map(position => {
                                    const runners = (race.finishers || []).filter(r => r.position === position);
                                    return <span className="race-result-place" key={position}>
                                      <small>{['1st', '2nd', '3rd', '4th'][position - 1]}</small>
                                      {runners.length ? runners.map(r => <span key={r.number}
                                        className={`race-result-number core-rank-${r.core_rank || 0}${r.brain_top ? ' brain-top' : ''}`}
                                        title={`${r.runner} · ${position} place · ${r.core_rank ? 'Core rank ' + r.core_rank : 'Core rank unavailable'}${r.brain_top ? ' · Brain rank 1' : ''}`}
                                        aria-label={`${position} place, number ${r.number}, ${r.runner}, ${r.core_rank ? 'Core rank ' + r.core_rank : 'Core rank unavailable'}`}>{r.number}</span>)
                                        : <span className="race-result-number" title="Result pending">—</span>}
                                    </span>;
                                  })}
                                </div>
                              ) : <><div className="race-class-text">
                                {formatRaceClass(race.race_class_conditions, race.race_name)}
                              </div><div className="race-distance">{race.race_distance}m</div></>}
                              {race.race_completed && race.result_state === 'pending' && <small className="race-result-status">Results pending</small>}
                              {race.race_completed && race.result_state === 'partial' && <small className="race-result-status">{race.results_source === 'BETFAIR_WINNER' ? 'Winner: Betfair · places pending' : 'Partial result · places pending'}</small>}
                            </Link>
                          ) : (
                            <div className="empty-race"></div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <InternationalSection date={getEffectiveDateString()} search={searchTerm} />
    </div>
  );
};

export default Dashboard;

